import { Injectable } from '@angular/core';
import { DashboardMeasureDetailProd } from '@models/dashboard-measure-details.model';
import { MULTI_PERFORMANCE_TYPE } from '@constants/mips-constants';
import { DashboardMeasureDecile } from '@models/dashboard-measure-deciles.model';
import measureDataJson from '../components/dashboard/measureData.json';

@Injectable({
  providedIn: 'root'
})
export class MipsCalculationService {

  constructor() { }

  /**
   * Checks if a measure is an inverse measure
   * @param measure The dashboard measure
   * @returns True if the measure is inverse, false otherwise
   */
  isInverseMeasure(measure: DashboardMeasureDetailProd): boolean {
    if (measure.inverse === undefined) {
      return false;
    }
    return measure.inverse;
  }

  isDesignatedChild(measure: DashboardMeasureDetailProd): boolean {
    if (!measure.isMultiPerformance) return true;
    if (measure.isMultiPerformanceParent) return true;
    const performanceType = measure.performanceType;
    if (performanceType === MULTI_PERFORMANCE_TYPE.WEIGHTED || performanceType === MULTI_PERFORMANCE_TYPE.SIMPLE_AVERAGE) return false;
    return measure.populationNbr?.toString() === performanceType;
  }

  /**
   * Finds the sibling whose populationNbr matches typeValue. Returns undefined for "W"/"S",
   * since those name a combination mode rather than a population.
   */
  private findSiblingByPopulationRef(
    typeValue: string | null | undefined,
    relatedMeasures: DashboardMeasureDetailProd[]
  ): DashboardMeasureDetailProd | undefined {
    if (!typeValue) return undefined;
    if (typeValue === MULTI_PERFORMANCE_TYPE.WEIGHTED || typeValue === MULTI_PERFORMANCE_TYPE.SIMPLE_AVERAGE) return undefined;
    return relatedMeasures.find((relatedMeasure) => relatedMeasure.populationNbr?.toString() === typeValue);
  }

  /**
   * The row that actually determines this measure's score: the matching population, or
   * the parent for a weighted/simple-average family.
   */
  getScoringMeasure(
    measure: DashboardMeasureDetailProd,
    relatedMeasures: DashboardMeasureDetailProd[]
  ): DashboardMeasureDetailProd | undefined {
    return (
      this.findSiblingByPopulationRef(measure.performanceType, relatedMeasures) ??
      relatedMeasures.find((relatedMeasure) => relatedMeasure.isMultiPerformanceParent)
    );
  }

  /**
   * The sibling population whose caseMinimumMet/completeness should be used instead of this
   * measure's own, per caseMinimumType (e.g. CMS-138 scores population 2's rate but judges
   * case-minimum against population 1's volume).
   */
  resolveCaseMinSibling(
    measure: DashboardMeasureDetailProd,
    relatedMeasures: DashboardMeasureDetailProd[]
  ): DashboardMeasureDetailProd {
    const { caseMinimumType, populationNbr } = measure;
    if (caseMinimumType === populationNbr?.toString()) return measure;
    return this.findSiblingByPopulationRef(caseMinimumType, relatedMeasures) ?? measure;
  }

  /**
   * Applies floor and cap logic to calculated points
   * @param value The calculated points value
   * @param floor The minimum points allowed
   * @param has7PointCap Whether the measure has a 7-point cap
   * @returns The adjusted points value
   */
  checkFloorAndCap(value: number, floor: number, has7PointCap: boolean): number {
    if (floor > 0) {
      if (value < floor) {
        value = floor;
      }
    }

    if (has7PointCap) {
      if (value > 7) {
        value = 7;
      }
    }

    return value;
  }

  /**
   * Calculates the CMS achievement points for a quality measure.
   *
   * @param isInverse      True ⇢ lower performanceRate earns more points.
   * @param floor          Minimum points allowed for this measure.
   * @param has7PointCap   When true, truncate anything above 7 pts.
   * @param measureName    The numeric/alpha measure identifier.
   * @param performanceRate The clinician's performance rate.
   * @param isSmallPractice Whether the practice is considered small.
   * @param noBenchmark    Whether the measure has no benchmark.
   * @param caseMinimumMet Whether the measure meets the 20-patient case minimum.
   * @param completeness   Data completeness rate (0–1). Must be ≥ 0.75 for any points; floor does not apply if below.
   * @param measureDeciles The decile data for calculating points.
   * @returns The calculated achievement points
   */
  getAchievementPoints(
    isInverse: boolean,
    floor: number,
    has7PointCap: boolean | undefined,
    measureName: string,
    performanceRate: number,
    isSmallPractice: boolean = false,
    noBenchmark: boolean = false,
    caseMinimumMet: boolean = false,
    completeness: number = 0,
    measureDeciles: DashboardMeasureDecile[]
  ): number {

    // Data completeness must be met for any points — floor does not apply here.
    if (completeness < 0.75) {
      return 0;
    }

    if (isSmallPractice && noBenchmark) {
      return this.checkFloorAndCap(3, floor, has7PointCap ?? false);
    }

    if (isSmallPractice && !caseMinimumMet) {
      return this.checkFloorAndCap(3, floor, has7PointCap ?? false);
    }

    if (!isSmallPractice && noBenchmark) {
      return this.checkFloorAndCap(0, floor, has7PointCap ?? false);
    }

    if (!isSmallPractice && !caseMinimumMet) {
      return this.checkFloorAndCap(0, floor, has7PointCap ?? false);
    }

    const deciles = measureDeciles
      .filter(d => (measureName ?? '').toLowerCase() === d.measureID.toLowerCase())
      .filter(d => d.lowerLimit !== -1 && d.upperLimit !== -1)
      .sort((a, b) =>
        isInverse ? b.lowerLimit - a.lowerLimit : a.lowerLimit - b.lowerLimit
      );

    if (!deciles.length) {
      return this.checkFloorAndCap(isSmallPractice ? 3 : 0, floor, has7PointCap ?? false);
    }


    const adjustFraction = (f: number): number => {
      if (f < 0.01) return 0;
      if (f >= 1.0) return 0.99;
      return Math.round(f * 100) / 100;
    };

    const pointsWithCaps = (raw: number) =>
      this.checkFloorAndCap(raw, floor, has7PointCap ?? false);

    const decile10 = deciles.find(d => d.decile === 10);
    if (decile10 &&
        ((!isInverse && performanceRate >= decile10.lowerLimit) ||
         (isInverse && performanceRate <= decile10.lowerLimit))) {
      return pointsWithCaps(10);
    }

    for (let decileIndex = 0; decileIndex < deciles.length - 1; decileIndex++) {
      const current = deciles[decileIndex];
      const next    = deciles[decileIndex + 1];

      const inRange = isInverse
        ? performanceRate <= current.lowerLimit && performanceRate > next.lowerLimit
        : performanceRate >= current.lowerLimit && performanceRate < next.lowerLimit;

      if (!inRange) continue;

      const lowerBound = current.lowerLimit;
      const upperBound = next.lowerLimit;
      const fraction = isInverse
        ? (lowerBound - performanceRate) / (lowerBound - upperBound)
        : (performanceRate - lowerBound) / (upperBound - lowerBound);

      const partial = adjustFraction(fraction);
      const rawPts  = parseFloat((current.decile + partial).toFixed(2));

      return pointsWithCaps(rawPts);
    }

    return pointsWithCaps(0);
  }

  /**
   * Calculates achievement points for a specific measure.
   * @param measure The dashboard measure (provides performance, floor, cap, benchmark flags).
   * @param performanceValue The performance value as a decimal (e.g. 0.85 for 85%).
   * @param isSmallPractice Whether the practice is considered small.
   * @param measureDeciles The decile data for calculating points.
   * @param caseMinSource Optional override for completeness and caseMinimumMet — used when a
   *   cross-population measure sources its case minimum from a different population than the
   *   one being scored (i.e. caseMinimumType !== performanceType).
   * @returns The calculated achievement points.
   */
  getAchievementPointsForMeasure(
    measure: DashboardMeasureDetailProd,
    performanceValue: number,
    isSmallPractice: boolean,
    measureDeciles: DashboardMeasureDecile[],
    caseMinSource?: { completeness?: number | null; caseMinimumMet?: boolean | null }
  ): number {
    const completeness = caseMinSource?.completeness ?? measure.completeness ?? 0;
    const caseMinimumMet = caseMinSource?.caseMinimumMet ?? measure.caseMinimumMet ?? false;
    return this.getAchievementPoints(
      this.isInverseMeasure(measure),
      measure.floor ?? 0,
      measure.sevenPointCap,
      this.getBaseMeasureId(measure.name ?? ''),
      performanceValue * 100,
      isSmallPractice ?? false,
      measure.noBenchmark ?? false,
      caseMinimumMet,
      completeness,
      measureDeciles
    );
  }

  getBaseMeasureId(measureName: string): string {
    if (!measureName) return "";
    const cleanName = measureName.replace(/_\d{4}$/g, "");
    if (/^[a-zA-Z]+-\d+-\d+$/.test(cleanName)) {
      return cleanName.replace(/-\d+$/, '');
    }
    return cleanName;
  }

  filterEquivalentMeasures<T>(measures: T[], getId: (m: T) => string): T[] {
    const equivalentGroups: { [key: string]: string[] } = {};
    (measureDataJson.equivalentMeasures as any[]).forEach((eq) => {
      const group = [eq.measureId, eq.cmsEcqmId];
      const groupKey = group.sort().join("|");
      equivalentGroups[groupKey] = group;
    });

    const filtered: T[] = [];
    const usedGroups = new Set<string>();

    for (const measure of measures) {
      const measureId = this.getBaseMeasureId(getId(measure));
      let groupKey: string | null = null;
      for (const [key, group] of Object.entries(equivalentGroups)) {
        if (group.includes(measureId)) {
          groupKey = key;
          break;
        }
      }

      if (groupKey) {
        if (!usedGroups.has(groupKey)) {
          filtered.push(measure);
          usedGroups.add(groupKey);
        }
      } else {
        filtered.push(measure);
      }
    }

    return filtered;
  }

}
